import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";
import { Card, CardContent } from "@/ui/components/ui/card";
import { useGetMachinesByLocation } from "@/machines/api/queries/machines.queries.ts";
import { useParams } from "react-router-dom";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore.ts";
import { useAggregatedMetadata } from "@/machines/hooks/use-aggregated-metadata.ts";

interface KPIItem {
  label: string;
  value: string | number;
}

interface KPIStripeProps {
  items?: KPIItem[];
}

const defaultKPIs: KPIItem[] = [];

const formatTime = (
  seconds: number | undefined,
  t: TFunction<"machines">,
): string => {
  if (seconds == null || !Number.isFinite(seconds)) return "-";
  const minutes = Math.round(seconds / 60);
  return t("units.minutes", { value: formatNumber(minutes) });
};

const KPIStripe = ({ items = defaultKPIs }: KPIStripeProps) => {
  const { t } = useTranslation("machines");
  const { id } = useParams();
  const { value } = useDateTimeRangeStore();
  const { data: machines } = useGetMachinesByLocation(id);

  const deviceMxIds = (machines?.data ?? []).map((m) => m.device_mxid).filter(Boolean);

  const { data: agg } = useAggregatedMetadata({
    deviceMxIds,
    start: value.fromDate,
    end: value.toDate,
  });

  const computedItems: KPIItem[] = items.length > 0 ? items : [
    { label: t("card.kpis.avgCycleTime"), value: agg ? formatTime(agg.aggregated.truckLoading.avgCycleTime, t) : "-" },
    { label: t("card.kpis.targetCycleTime"), value: agg ? formatTime(agg.aggregated.truckLoading.targetCycleTime, t) : "-" },
    { label: t("card.kpis.longCycles"), value: agg ? t("card.kpis.countWithPercent", { value: formatNumber(agg.aggregated.truckLoading.longCyclesCnt), percent: t("units.percent", { value: formatNumber(agg.aggregated.truckLoading.longCyclesPerc, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) }) }) : "-" },
    { label: t("card.kpis.bucketCount"), value: agg?.aggregated.truckLoading.bucketCount ?? "-" },
    { label: t("card.kpis.avgWaitTime"), value: agg ? formatTime(agg.aggregated.waitingForTruck.avgWaitTime, t) : "-" },
    { label: t("card.kpis.longWaits"), value: agg ? t("card.kpis.countWithPercent", { value: formatNumber(agg.aggregated.waitingForTruck.longWaitsCnt), percent: t("units.percent", { value: formatNumber(agg.aggregated.waitingForTruck.longWaitsPerc, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) }) }) : "-" },
    { label: t("card.kpis.boulderCount"), value: agg?.aggregated.truckLoading.boulderCount ?? "-" },
  ];

  return (
    <div className="w-full bg-sidebar/50 border-y">
      <div className="flex gap-4 py-3 px-4">
        {computedItems.map((item, index) => (
          <Card
            key={index}
            className="flex-1 bg-background/50 border-muted"
          >
            <CardContent className="p-2 flex flex-col gap-1 justify-between">
              <span className="text-xs text-muted-foreground text-center">
                {item.label}
              </span>
              <span className="text-lg font-bold text-center">
                {item.value}
              </span>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default KPIStripe;
